// Bespoke Iron Covenant silhouettes whose appearance cannot be communicated by the shared
// humanoid part pool: paired bell-golems, five unique Exemplars, and the Fortress Ram.
// Fully procedural (no asset files, plan §29.4); local +z is forward.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function mat(color: THREE.Color | number, roughness = 0.78, metalness = 0.08): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness, flatShading: true });
}

function emissive(color: number, intensity = 1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.3,
    metalness: 0.25,
    flatShading: true,
  });
}

function shadows(group: THREE.Group): void {
  group.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
}

export interface IronSquadPose {
  time: number;
  phase: number;
  moveAmt: number;
  strike: number;
}

export interface IronSquadRig {
  group: THREE.Group;
  pose: (frame: IronSquadPose) => void;
}

export const BESPOKE_IRON_SQUAD_DEFS = new Set([
  'u-ic-bastion-golems',
  'u-ic-bastion-wagon',
  'u-ic-kings-exemplars',
  'u-ic-vanguard',
  'u-ic-shield-legion',
  'u-ic-pike-wardens',
  'u-ic-longshot-snipers',
  'u-ic-banner-captain',
  'u-ic-master-builder',
  'u-ic-crossbow-retinue',
  'u-ic-oathbreaker-hunters',
  'u-ic-royal-artificers',
  'u-ic-forge-clerics',
]);

function limb(name: string, material: THREE.Material, x: number, y: number, length: number, radius: number): THREE.Group {
  const pivot = new THREE.Group();
  pivot.name = name;
  pivot.position.set(x, y, 0);
  const segment = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.82, radius, length, 7), material);
  segment.position.y = -length / 2;
  pivot.add(segment);
  return pivot;
}

function bastionGolem(owner: 0 | 1, memberIndex: number, uiScale: number): IronSquadRig {
  const group = new THREE.Group();
  const stone = mat(mix(memberIndex ? 0xc2b9a7 : 0xd0c8b8, OWNER_COLORS[owner], 0.08), 0.94);
  const stoneDark = mat(0x7b756b, 0.96);
  const iron = mat(mix(0x30353b, OWNER_COLORS[owner], 0.12), 0.48, 0.62);
  const rune = emissive(0xf2b84b, 1.25);

  const pelvis = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.34, 0.52), iron);
  pelvis.position.y = 0.82;
  group.add(pelvis);
  const torso = new THREE.Mesh(new THREE.BoxGeometry(1.08, 0.92, 0.64), stone);
  torso.name = 'torso';
  torso.position.y = 1.43;
  group.add(torso);
  const chestBand = new THREE.Mesh(new THREE.BoxGeometry(1.13, 0.16, 0.68), iron);
  chestBand.position.y = 1.52;
  group.add(chestBand);

  // The open cage and luminous bell make Retaliation Bell legible even at RTS zoom.
  const bell = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.29, 0.5, 10), rune);
  bell.name = 'bell';
  bell.position.set(0, 1.42, 0.39);
  group.add(bell);
  const bellLip = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.045, 6, 12), iron);
  bellLip.rotation.x = Math.PI / 2;
  bellLip.position.set(0, 1.18, 0.39);
  group.add(bellLip);
  for (const x of [-0.32, 0.32]) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.72, 0.06), iron);
    bar.position.set(x, 1.42, 0.48);
    group.add(bar);
  }

  // Battlement shoulders and block-like forearms sell the mobile-fortress silhouette.
  const armL = new THREE.Group();
  armL.name = 'armL';
  armL.position.set(-0.72, 1.7, 0);
  const armR = new THREE.Group();
  armR.name = 'armR';
  armR.position.set(0.72, 1.7, 0);
  for (const [arm, sx] of [[armL, -1], [armR, 1]] as const) {
    const shoulder = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.34, 0.62), stone);
    shoulder.position.set(sx * 0.04, 0, 0);
    arm.add(shoulder);
    const crenelA = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.2, 0.18), stoneDark);
    crenelA.position.set(sx * 0.04, 0.24, -0.18);
    arm.add(crenelA);
    const crenelB = crenelA.clone();
    crenelB.position.z = 0.18;
    arm.add(crenelB);
    const forearm = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.78, 0.52), stone);
    forearm.position.set(sx * 0.04, -0.56, 0.12);
    arm.add(forearm);
    group.add(arm);
  }
  const legL = limb('legL', stone, -0.31, 0.78, 0.82, 0.25);
  const legR = limb('legR', stone, 0.31, 0.78, 0.82, 0.25);
  for (const leg of [legL, legR]) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.24, 0.7), stoneDark);
    foot.position.set(0, -0.78, 0.12);
    leg.add(foot);
    group.add(leg);
  }

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 0.44), stone);
  head.name = 'head';
  head.position.y = 2.08;
  group.add(head);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.07, 0.04), rune);
  visor.position.set(0, 2.1, 0.24);
  group.add(visor);
  for (const sx of [-1, 0, 1]) {
    const merlon = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.22, 0.2), stoneDark);
    merlon.position.set(sx * 0.19, 2.4, 0);
    group.add(merlon);
  }

  group.scale.setScalar(uiScale);
  shadows(group);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike }) => {
      const stride = Math.sin(phase) * 0.32 * moveAmt;
      legL.rotation.x = stride;
      legR.rotation.x = -stride;
      armL.rotation.x = -stride * 0.55 - strike * 1.0;
      armR.rotation.x = stride * 0.55 - strike * 1.0;
      torso.rotation.z = Math.sin(phase) * 0.025 * moveAmt;
      bell.scale.setScalar(1 + Math.sin(time * 3.2 + memberIndex) * 0.055 + strike * 0.16);
      rune.emissiveIntensity = 1.1 + strike * 1.6 + Math.sin(time * 2.1 + memberIndex) * 0.15;
    },
  };
}

function blade(material: THREE.Material, length: number, width: number): THREE.Group {
  const g = new THREE.Group();
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.32, 6), mat(0x5a422c, 0.78));
  grip.position.y = 0.1;
  g.add(grip);
  const edge = new THREE.Mesh(new THREE.BoxGeometry(width, length, 0.055), material);
  edge.position.y = 0.32 + length / 2;
  g.add(edge);
  const point = new THREE.Mesh(new THREE.ConeGeometry(width * 0.72, width * 1.5, 4), material);
  point.position.y = 0.32 + length + width * 0.65;
  point.rotation.y = Math.PI / 4;
  g.add(point);
  const guard = new THREE.Mesh(new THREE.BoxGeometry(width * 2.2, 0.07, 0.08), mat(0xd8b85b, 0.42, 0.6));
  guard.position.y = 0.3;
  g.add(guard);
  return g;
}

function exemplar(owner: 0 | 1, memberIndex: number, uiScale: number): IronSquadRig {
  const variant = memberIndex % 5;
  const group = new THREE.Group();
  const plate = mat(mix(0xe8e2d2, OWNER_COLORS[owner], 0.1), 0.38, 0.58);
  const gold = mat(0xd8b85b, 0.35, 0.66);
  const dark = mat(0x31343a, 0.62, 0.45);
  const steel = mat(0xd7dce2, 0.28, 0.78);
  const cloth = mat(mix(0xebe6d8, OWNER_COLORS[owner], 0.3), 0.9);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.33, 0.78, 8), plate);
  torso.name = 'torso';
  torso.position.y = 1.17;
  group.add(torso);
  const waist = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.13, 0.38), gold);
  waist.position.y = 0.83;
  group.add(waist);
  const cape = new THREE.Mesh(
    new THREE.PlaneGeometry(0.62, 0.82),
    new THREE.MeshStandardMaterial({ color: cloth.color, roughness: 0.9, side: THREE.DoubleSide }),
  );
  cape.position.set(0, 1.1, -0.28);
  cape.rotation.x = 0.12;
  group.add(cape);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 9, 7), plate);
  head.name = 'head';
  head.position.y = 1.72;
  group.add(head);
  const helm = variant === 1
    ? new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.46, 7), plate)
    : new THREE.Mesh(new THREE.SphereGeometry(0.235, 9, 6, 0, Math.PI * 2, 0, Math.PI / 2), plate);
  helm.position.y = variant === 1 ? 1.92 : 1.75;
  group.add(helm);
  const crest = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.34, 0.44), variant === 4 ? dark : gold);
  crest.position.set(0, 2.0, -0.02);
  group.add(crest);

  const legL = limb('legL', plate, -0.18, 0.78, 0.72, 0.12);
  const legR = limb('legR', plate, 0.18, 0.78, 0.72, 0.12);
  const armL = limb('armL', plate, -0.4, 1.42, 0.68, 0.1);
  const armR = limb('armR', plate, 0.4, 1.42, 0.68, 0.1);
  group.add(legL, legR, armL, armR);

  const weapon = new THREE.Group();
  weapon.name = 'weapon';
  weapon.position.set(0.42, 0.9, 0.2);
  if (variant === 0) {
    const greatsword = blade(steel, 1.15, 0.17);
    greatsword.rotation.x = 0.24;
    weapon.add(greatsword);
  } else if (variant === 1) {
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 1.65, 6), dark);
    shaft.position.y = 0.8;
    weapon.add(shaft);
    const axe = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.34, 0.08), steel);
    axe.position.set(0.17, 1.55, 0);
    weapon.add(axe);
  } else if (variant === 2) {
    weapon.add(blade(steel, 0.78, 0.11));
    const shield = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.34, 0.08, 5), plate);
    shield.rotation.x = Math.PI / 2;
    shield.position.set(-0.85, 0.52, 0.22);
    weapon.add(shield);
    const boss = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.1, 8), gold);
    boss.rotation.x = Math.PI / 2;
    boss.position.set(-0.85, 0.52, 0.27);
    weapon.add(boss);
  } else if (variant === 3) {
    const haft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 1.2, 6), dark);
    haft.position.y = 0.58;
    weapon.add(haft);
    const hammer = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.34, 0.34), steel);
    hammer.position.y = 1.2;
    weapon.add(hammer);
    const crown = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.1, 0.38), gold);
    crown.position.y = 1.4;
    weapon.add(crown);
  } else {
    const right = blade(steel, 0.68, 0.09);
    right.position.x = 0.12;
    weapon.add(right);
    const left = blade(steel, 0.68, 0.09);
    left.position.set(-0.82, 0, 0);
    left.rotation.z = -0.16;
    weapon.add(left);
  }
  group.add(weapon);

  // Small dark repair patches provide campaign history without muddying the white-gold read.
  const scar = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.05, 0.05), dark);
  scar.position.set((variant % 2 ? -1 : 1) * 0.18, 1.27, 0.31);
  scar.rotation.z = (variant - 2) * 0.18;
  group.add(scar);

  group.scale.setScalar(uiScale);
  shadows(group);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike }) => {
      const stride = Math.sin(phase) * 0.58 * moveAmt;
      legL.rotation.x = stride;
      legR.rotation.x = -stride;
      armL.rotation.x = -stride * 0.55 - strike * (variant === 4 ? 0.85 : 0.35);
      armR.rotation.x = stride * 0.55 - strike * 0.9;
      weapon.rotation.x = 0.25 - strike * (variant === 1 || variant === 3 ? 1.25 : 0.9);
      torso.rotation.z = Math.sin(phase) * 0.035 * moveAmt;
      head.rotation.y = Math.sin(time * 0.55 + memberIndex) * 0.1 * (1 - moveAmt);
    },
  };
}

function covenantSoldier(defId: string, owner: 0 | 1, memberIndex: number, uiScale: number): IronSquadRig {
  const group = new THREE.Group();
  const heavy = defId === 'u-ic-shield-legion' || defId === 'u-ic-banner-captain';
  const ranged = defId === 'u-ic-longshot-snipers' || defId === 'u-ic-crossbow-retinue';
  const support = defId === 'u-ic-master-builder' || defId === 'u-ic-royal-artificers' || defId === 'u-ic-forge-clerics';
  const oathbreaker = defId === 'u-ic-oathbreaker-hunters';
  const plate = mat(mix(oathbreaker ? 0x292d34 : heavy ? 0xaeb6c0 : 0xc7cbd0, OWNER_COLORS[owner], 0.14), 0.42, 0.58);
  const dark = mat(oathbreaker ? 0x171a20 : 0x303740, 0.5, 0.62);
  const gold = mat(support ? 0xd9a53d : 0xb89149, 0.34, 0.72);
  const cloth = mat(mix(oathbreaker ? 0x24262d : 0x6e7890, OWNER_COLORS[owner], 0.34), 0.92);
  const steel = mat(0xd8dde2, 0.28, 0.78);
  const heat = emissive(defId === 'u-ic-forge-clerics' ? 0xff8a2b : oathbreaker ? 0x7f91aa : 0xffce65, 1.1);

  const torso = new THREE.Mesh(
    heavy ? new THREE.BoxGeometry(0.68, 0.82, 0.46) : new THREE.CylinderGeometry(0.23, 0.31, 0.78, 8),
    plate,
  );
  torso.name = 'torso';
  torso.position.y = 1.18;
  group.add(torso);
  const breast = new THREE.Mesh(new THREE.BoxGeometry(heavy ? 0.72 : 0.52, 0.34, 0.12), dark);
  breast.position.set(0, 1.3, 0.25);
  group.add(breast);
  const belt = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.12, 0.38), gold);
  belt.position.y = 0.84;
  group.add(belt);
  const cape = new THREE.Mesh(new THREE.PlaneGeometry(0.56, 0.74), cloth);
  cape.position.set(0, 1.1, -0.28);
  cape.rotation.x = 0.1;
  group.add(cape);

  const legL = limb('legL', plate, -0.17, 0.78, 0.72, heavy ? 0.13 : 0.1);
  const legR = limb('legR', plate, 0.17, 0.78, 0.72, heavy ? 0.13 : 0.1);
  const armL = limb('armL', plate, heavy ? -0.46 : -0.39, 1.43, 0.67, heavy ? 0.13 : 0.09);
  const armR = limb('armR', plate, heavy ? 0.46 : 0.39, 1.43, 0.67, heavy ? 0.13 : 0.09);
  group.add(legL, legR, armL, armR);

  const head = new THREE.Group();
  head.name = 'head';
  head.position.y = 1.72;
  group.add(head);
  const helm = new THREE.Mesh(new THREE.SphereGeometry(0.22, 9, 6, 0, Math.PI * 2, 0, Math.PI / 1.75), plate);
  helm.rotation.x = -0.08;
  head.add(helm);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.075, 0.055), oathbreaker ? heat : dark);
  visor.position.set(0, 0, 0.2);
  head.add(visor);
  if (defId === 'u-ic-pike-wardens' || defId === 'u-ic-banner-captain') {
    const crest = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.38, 0.34), gold);
    crest.position.y = 0.26;
    head.add(crest);
  }

  const weapon = new THREE.Group();
  weapon.name = 'weapon';
  weapon.position.set(0, -0.58, 0.16);
  if (defId === 'u-ic-pike-wardens') {
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, 2.35, 6), dark);
    shaft.position.y = 0.72;
    weapon.add(shaft);
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.42, 5), steel);
    point.position.y = 2.08;
    weapon.add(point);
  } else if (defId === 'u-ic-longshot-snipers') {
    for (const x of [-0.08, 0.08]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(0.045, 1.65, 0.07), dark);
      rail.position.x = x;
      weapon.add(rail);
    }
    const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.16, 10), heat);
    lens.rotation.z = Math.PI / 2;
    lens.position.set(0.12, 0.38, 0);
    weapon.add(lens);
  } else if (defId === 'u-ic-crossbow-retinue') {
    const stock = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.95, 0.12), dark);
    weapon.add(stock);
    const bow = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.08, 0.1), steel);
    bow.position.y = 0.55;
    weapon.add(bow);
  } else if (support) {
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 1.25, 6), dark);
    shaft.position.y = 0.38;
    weapon.add(shaft);
    const tool = defId === 'u-ic-master-builder'
      ? new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.18, 0.18), steel)
      : defId === 'u-ic-forge-clerics'
        ? new THREE.Mesh(new THREE.IcosahedronGeometry(0.24, 0), heat)
        : new THREE.Mesh(new THREE.OctahedronGeometry(0.25, 0), gold);
    tool.position.y = 1.08;
    weapon.add(tool);
  } else {
    weapon.add(blade(oathbreaker ? dark : steel, 0.86, 0.12));
  }
  weapon.rotation.x = ranged ? 1.08 : defId === 'u-ic-pike-wardens' ? 0.32 : 0.44;
  armL.add(weapon);

  if (defId === 'u-ic-shield-legion' || defId === 'u-ic-vanguard') {
    const shield = new THREE.Mesh(
      defId === 'u-ic-shield-legion' ? new THREE.BoxGeometry(0.62, 1.18, 0.13) : new THREE.CylinderGeometry(0.38, 0.52, 0.12, 6),
      plate,
    );
    shield.position.set(0.5, 1.04, 0.34);
    shield.rotation.z = defId === 'u-ic-shield-legion' ? 0 : Math.PI / 2;
    group.add(shield);
    const boss = new THREE.Mesh(new THREE.OctahedronGeometry(0.12, 0), gold);
    boss.position.set(0.5, 1.05, 0.43);
    group.add(boss);
  }
  if (defId === 'u-ic-banner-captain') {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.035, 2.15, 5), dark);
    pole.position.set(-0.34, 1.65, -0.18);
    group.add(pole);
    const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.52), cloth);
    flag.position.set(0, 2.35, -0.18);
    group.add(flag);
  }
  group.scale.setScalar(uiScale);
  shadows(group);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike }) => {
      const stride = Math.sin(phase) * (heavy ? 0.48 : 0.62) * moveAmt;
      legL.rotation.x = stride;
      legR.rotation.x = -stride;
      armL.rotation.x = -stride * 0.52 - strike * (ranged ? 0.18 : 0.92);
      armR.rotation.x = stride * 0.52 - strike * (ranged ? 0.12 : 0.5);
      weapon.rotation.x = (ranged ? 1.08 : defId === 'u-ic-pike-wardens' ? 0.32 : 0.44) - strike * (ranged ? 0.12 : 1.0);
      weapon.position.z = 0.16 - strike * (ranged ? 0.18 : -0.08);
      torso.rotation.z = Math.sin(phase) * 0.025 * moveAmt;
      head.rotation.y = Math.sin(time * 0.55 + memberIndex) * 0.1 * (1 - moveAmt);
      cape.rotation.x = 0.1 + moveAmt * 0.18 + Math.sin(time * 1.1 + memberIndex) * 0.025;
    },
  };
}

/**
 * Bastion Wagon (Core Annex): two mechanical oxen with piston legs and furnace eyes
 * hauling a riveted armored wagon — stacked fort segments and a tarped folded ballista
 * on the bed, six rolling wheels. Single-member squad; local +z is forward.
 */
function bastionWagon(owner: 0 | 1, uiScale: number): IronSquadRig {
  const group = new THREE.Group();
  const team = OWNER_COLORS[owner];
  const hull = mat(mix(0x5b636e, team, 0.16), 0.45, 0.6);
  const ironDark = mat(0x2c3138, 0.55, 0.55);
  const brass = mat(0xc29e4e, 0.38, 0.7);
  const wood = mat(0x54402d, 0.9, 0.04);
  const canvas = mat(mix(0x9a8f74, team, 0.1), 0.95, 0);
  const stone = mat(0xa39a88, 0.94, 0.02);
  const furnace = emissive(0xff9c3f, 1.5);

  // ---- two mechanical oxen abreast (forward)
  interface Ox { legs: THREE.Group[]; head: THREE.Group }
  const oxen: Ox[] = [];
  for (const ox of [-1, 1]) {
    const beast = new THREE.Group();
    beast.position.set(ox * 0.52, 0, 1.75);
    group.add(beast);
    const bodyBox = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.56, 1.15), hull);
    bodyBox.position.y = 0.92;
    beast.add(bodyBox);
    const spineVent = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.1, 0.85), brass);
    spineVent.position.y = 1.25;
    beast.add(spineVent);
    const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.3, 6), ironDark);
    stack.position.set(ox * 0.18, 1.36, -0.35);
    beast.add(stack);
    // boxy head with horns + glowing furnace eyes
    const head = new THREE.Group();
    head.name = `oxHead${ox < 0 ? 'L' : 'R'}`;
    head.position.set(0, 0.98, 0.72);
    beast.add(head);
    const skull = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.34, 0.42), ironDark);
    head.add(skull);
    const snout = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.2, 0.16), brass);
    snout.position.set(0, -0.1, 0.26);
    head.add(snout);
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.03), furnace);
      eye.position.set(sx * 0.11, 0.06, 0.22);
      head.add(eye);
      const horn = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.34, 5), brass);
      horn.position.set(sx * 0.26, 0.18, 0.02);
      horn.rotation.z = sx * -1.15;
      head.add(horn);
    }
    // grate glowing in the chest
    const grate = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.2, 0.04), furnace);
    grate.position.set(0, 0.85, 0.6);
    beast.add(grate);
    // piston legs: dark cylinder sleeve with a bright piston rod inside
    const legs: THREE.Group[] = [];
    for (const [lx, lz] of [[-0.24, 0.38], [0.24, 0.38], [-0.24, -0.38], [0.24, -0.38]] as const) {
      const leg = new THREE.Group();
      leg.position.set(lx, 0.78, lz);
      const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.08, 0.42, 6), ironDark);
      sleeve.position.y = -0.2;
      leg.add(sleeve);
      const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.44, 5), brass);
      rod.name = 'piston';
      rod.position.y = -0.5;
      leg.add(rod);
      const shoe = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.1, 0.24), ironDark);
      shoe.position.y = -0.74;
      leg.add(shoe);
      beast.add(leg);
      legs.push(leg);
    }
    oxen.push({ legs, head });
  }
  // yoke joining the pair, and a drawbar back to the wagon
  const yoke = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.12, 0.16), wood);
  yoke.position.set(0, 1.05, 1.3);
  group.add(yoke);
  const drawbar = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 1.4, 6), wood);
  drawbar.rotation.x = Math.PI / 2;
  drawbar.position.set(0, 0.7, 0.55);
  group.add(drawbar);

  // ---- the armored wagon (rear)
  const bed = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.4, 2.3), hull);
  bed.position.set(0, 0.85, -1.0);
  group.add(bed);
  for (const sx of [-1, 1]) {
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.72, 2.4), hull);
    plate.position.set(sx * 0.82, 1.05, -1.0);
    group.add(plate);
    // rivet studs down each armor plate
    for (let r = 0; r < 4; r++) {
      const rivet = new THREE.Mesh(new THREE.SphereGeometry(0.045, 5, 4), brass);
      rivet.position.set(sx * 0.9, 1.28, -0.2 - r * 0.55);
      group.add(rivet);
    }
  }
  const tailgate = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.62, 0.12), ironDark);
  tailgate.position.set(0, 1.0, -2.18);
  group.add(tailgate);
  // stacked fort segments: two crenellated wall pieces racked at an angle
  for (let sSeg = 0; sSeg < 2; sSeg++) {
    const segment = new THREE.Group();
    segment.position.set(-0.25 + sSeg * 0.42, 1.32 + sSeg * 0.12, -1.55);
    segment.rotation.z = 0.18 + sSeg * 0.1;
    const wall = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.85, 1.05), stone);
    segment.add(wall);
    for (const mz of [-0.35, 0, 0.35]) {
      const merlon = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.18, 0.2), stone);
      merlon.position.set(0, 0.5, mz);
      segment.add(merlon);
    }
    group.add(segment);
  }
  // folded ballista under a lashed tarp at the front of the bed
  const tarp = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.5, 1.0), canvas);
  tarp.position.set(0.05, 1.28, -0.45);
  tarp.rotation.z = 0.06;
  group.add(tarp);
  const tarpFold = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.16, 0.5), canvas);
  tarpFold.position.set(0.02, 1.5, -0.62);
  tarpFold.rotation.x = 0.3;
  group.add(tarpFold);
  for (const rz of [-0.2, -0.72]) {
    const rope = new THREE.Mesh(new THREE.BoxGeometry(1.28, 0.035, 0.045), ironDark);
    rope.position.set(0.05, 1.34, rz);
    group.add(rope);
  }
  // ballista bow limbs + bolt tips poking out from under the canvas
  for (const sx of [-1, 1]) {
    const bowLimb = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.055, 0.75, 6), wood);
    bowLimb.position.set(sx * 0.62, 1.32, 0.02);
    bowLimb.rotation.z = sx * (Math.PI / 2 - 0.35);
    group.add(bowLimb);
  }
  const boltTip = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.24, 6), brass);
  boltTip.rotation.x = Math.PI / 2;
  boltTip.position.set(0.12, 1.42, 0.12);
  group.add(boltTip);

  // six wheels
  const wheels: THREE.Mesh[] = [];
  const wheelGeo = new THREE.CylinderGeometry(0.44, 0.44, 0.16, 10);
  wheelGeo.rotateZ(Math.PI / 2);
  for (let i = 0; i < 6; i++) {
    const wheel = new THREE.Mesh(wheelGeo, ironDark);
    wheel.name = `wheel${i}`;
    wheel.position.set(i % 2 === 0 ? -0.92 : 0.92, 0.44, -0.25 - (i >> 1) * 0.78);
    group.add(wheel);
    wheels.push(wheel);
  }

  // owner pennant on the tailgate
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.15, 5), ironDark);
  pole.position.set(-0.55, 2.0, -2.15);
  group.add(pole);
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.46, 0.3),
    new THREE.MeshStandardMaterial({ color: team, roughness: 0.85, side: THREE.DoubleSide }),
  );
  flag.position.set(-0.31, 2.42, -2.15);
  group.add(flag);

  // blob shadow (vehicles ground poorly on shadow maps alone at RTS zoom)
  const blob = new THREE.Mesh(
    new THREE.CircleGeometry(1.6, 20),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false }),
  );
  blob.rotation.x = -Math.PI / 2;
  blob.position.y = 0.015;
  group.add(blob);

  group.scale.setScalar(uiScale);
  shadows(group);
  return {
    group,
    pose: ({ time, phase, moveAmt, strike }) => {
      for (const wheel of wheels) wheel.rotation.x = phase * 0.8;
      for (let o = 0; o < oxen.length; o++) {
        const beastPhase = phase + o * Math.PI * 0.5;
        const legs = oxen[o].legs;
        const stride = Math.sin(beastPhase) * (0.03 + moveAmt * 0.45);
        legs[0].rotation.x = stride;
        legs[3].rotation.x = stride;
        legs[1].rotation.x = -stride;
        legs[2].rotation.x = -stride;
        // pistons pump on the swing leg
        for (let l = 0; l < legs.length; l++) {
          const piston = legs[l].getObjectByName('piston');
          if (piston) piston.position.y = -0.5 + Math.max(0, Math.sin(beastPhase + l * Math.PI)) * 0.08 * moveAmt;
        }
        const head = oxen[o].head;
        head.rotation.x = Math.sin(beastPhase) * 0.06 * moveAmt + Math.sin(time * 0.7 + o) * 0.03 * (1 - moveAmt);
        head.rotation.y = Math.sin(time * 0.45 + o * 2) * 0.08 * (1 - moveAmt);
      }
      furnace.emissiveIntensity = 1.5 + moveAmt * 0.6 + Math.sin(time * 3.1) * 0.25 + strike * 0.8;
    },
  };
}

export function makeIronSquadRig(defId: string, owner: 0 | 1, memberIndex: number, uiScale: number): IronSquadRig {
  if (defId === 'u-ic-bastion-golems') return bastionGolem(owner, memberIndex, uiScale);
  if (defId === 'u-ic-bastion-wagon') return bastionWagon(owner, uiScale);
  if (defId === 'u-ic-kings-exemplars') return exemplar(owner, memberIndex, uiScale);
  return covenantSoldier(defId, owner, memberIndex, uiScale);
}

/** Roofed mobile gatehouse with a chained crowned-lion ram. Named wheels roll and `ram` recoils. */
export function makeFortressRamGroup(owner: 0 | 1): THREE.Group {
  const group = new THREE.Group();
  const team = OWNER_COLORS[owner];
  const wood = mat(0x54402d, 0.9, 0.04);
  const iron = mat(mix(0x353a40, team, 0.14), 0.48, 0.62);
  const ironDark = mat(0x20242a, 0.56, 0.58);
  const trim = mat(0xc29e4e, 0.38, 0.7);
  const heat = emissive(0xffa22f, 1.3);

  const chassis = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.5, 3.5), wood);
  chassis.position.y = 0.65;
  group.add(chassis);
  for (const sx of [-1, 1]) {
    const wall = new THREE.Mesh(new THREE.BoxGeometry(0.24, 1.75, 3.35), iron);
    wall.position.set(sx * 1.03, 1.55, -0.05);
    group.add(wall);
    for (let z = -1.2; z <= 1.2; z += 0.8) {
      const brace = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.55, 0.12), trim);
      brace.position.set(sx * 1.17, 1.55, z);
      brace.rotation.z = sx * 0.12;
      group.add(brace);
    }
  }
  const rear = new THREE.Mesh(new THREE.BoxGeometry(2.25, 1.75, 0.22), iron);
  rear.position.set(0, 1.55, -1.7);
  group.add(rear);

  // Layered pitched roof plates protect the infantry-sized interior.
  for (const sx of [-1, 1]) {
    const roof = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.18, 3.85), ironDark);
    roof.position.set(sx * 0.58, 2.65, -0.02);
    roof.rotation.z = sx * -0.36;
    group.add(roof);
    for (const z of [-1.5, -0.75, 0, 0.75, 1.5]) {
      const rib = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.13, 0.18), trim);
      rib.position.set(sx * 0.58, 2.76, z);
      rib.rotation.z = roof.rotation.z;
      group.add(rib);
    }
  }
  for (const sx of [-1, 1]) {
    for (const z of [-1.25, 0, 1.25]) {
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.52, 5), ironDark);
      spike.position.set(sx * 1.24, 1.55, z);
      spike.rotation.z = sx * -Math.PI / 2;
      group.add(spike);
    }
  }

  const wheelGeo = new THREE.CylinderGeometry(0.47, 0.47, 0.18, 12);
  wheelGeo.rotateZ(Math.PI / 2);
  for (let i = 0; i < 6; i++) {
    const wheel = new THREE.Mesh(wheelGeo, ironDark);
    wheel.name = `wheel${i}`;
    wheel.position.set(i % 2 ? 1.25 : -1.25, 0.48, (Math.floor(i / 2) - 1) * 1.25);
    group.add(wheel);
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.22, 8), trim);
    hub.rotation.z = Math.PI / 2;
    hub.position.copy(wheel.position);
    hub.position.x += i % 2 ? 0.08 : -0.08;
    group.add(hub);
  }

  const ram = new THREE.Group();
  ram.name = 'ram';
  ram.position.set(0, 1.15, 0.1);
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.28, 3.7, 10), wood);
  beam.rotation.x = Math.PI / 2;
  beam.position.z = 0.2;
  ram.add(beam);
  for (const sx of [-1, 1]) {
    const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.5, 5), ironDark);
    chain.position.set(sx * 0.44, 0.72, -0.25);
    chain.rotation.z = sx * 0.42;
    ram.add(chain);
  }
  const mane = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.13, 7, 12), trim);
  mane.rotation.x = Math.PI / 2;
  mane.position.z = 2.12;
  ram.add(mane);
  const lion = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.52, 0.62), iron);
  lion.position.z = 2.2;
  ram.add(lion);
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.24, 0.32), ironDark);
  muzzle.position.set(0, -0.12, 2.58);
  ram.add(muzzle);
  for (const sx of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.3, 5), trim);
    ear.position.set(sx * 0.25, 0.34, 2.18);
    ear.rotation.z = sx * -0.5;
    ram.add(ear);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.065, 7, 5), heat);
    eye.position.set(sx * 0.16, 0.09, 2.52);
    ram.add(eye);
  }
  const crown = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.38, 5), trim);
  crown.position.set(0, 0.48, 2.2);
  ram.add(crown);
  group.add(ram);

  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.7, 0.42),
    new THREE.MeshStandardMaterial({ color: team, roughness: 0.85, side: THREE.DoubleSide }),
  );
  flag.position.set(0.38, 3.32, -1.25);
  group.add(flag);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 1.0, 5), trim);
  pole.position.set(0, 3.02, -1.25);
  group.add(pole);

  shadows(group);
  return group;
}
